// src/utils/htmlReceiptGenerator.js

export class HTMLReceiptGenerator {
  /**
   * Generate an HTML receipt that can be printed
   * @param {Object} sale - Sale data
   * @param {Object} options - Options for receipt generation
   * @returns {string} HTML content
   */
  static generateReceiptHTML(sale, options = {}) {
    const {
      companyName = 'Tezlaa Cafe',
      companyAddress = 'Your Business Address',
      companyPhone = 'Your Phone Number',
      companyEmail = 'contact@tezlaa.com'
    } = options

    console.log('🧾 generateReceiptHTML called with sale:', sale)

    // Handle different field names for items
    const items = sale.items || sale.orderItems || []
    console.log('🧾 Items found:', items.length, items)
    
    // Handle different field names for totals
    const subtotal = sale.subtotal || sale.orderPayment?.subtotal || 0
    const tax = sale.tax || sale.orderPayment?.serviceFee || sale.orderPayment?.totalFees || 0
    const discount = sale.discount || sale.orderPayment?.discount || 0
    const total = sale.total || sale.orderPayment?.totalAmount || 0
    
    console.log('🧾 Totals - subtotal:', subtotal, 'tax:', tax, 'discount:', discount, 'total:', total)

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Receipt - ${sale.orderNumber}</title>
    <style>
        body {
            font-family: 'Courier New', monospace;
            max-width: 300px;
            margin: 0 auto;
            padding: 20px;
            background: white;
            color: black;
        }
        .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 10px;
            margin-bottom: 15px;
        }
        .company-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
        }
        .company-info {
            font-size: 12px;
            line-height: 1.4;
        }
        .receipt-title {
            font-size: 16px;
            font-weight: bold;
            margin: 15px 0 10px 0;
        }
        .order-info {
            margin-bottom: 15px;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
        }
        .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            font-size: 12px;
        }
        .items-section {
            margin-bottom: 15px;
        }
        .items-header {
            border-bottom: 1px solid #000;
            padding-bottom: 5px;
            margin-bottom: 5px;
            font-weight: bold;
            font-size: 12px;
        }
        .item-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            font-size: 11px;
        }
        .item-name {
            flex: 1;
            margin-right: 10px;
        }
        .item-qty-price {
            text-align: right;
            min-width: 80px;
        }
        .totals-section {
            border-top: 1px dashed #000;
            padding-top: 10px;
            margin-bottom: 15px;
        }
        .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            font-size: 12px;
        }
        .total-row.final {
            font-weight: bold;
            font-size: 14px;
            border-top: 1px solid #000;
            padding-top: 5px;
            margin-top: 5px;
        }
        .payment-info {
            border-top: 1px dashed #000;
            padding-top: 10px;
            margin-bottom: 15px;
            font-size: 12px;
        }
        .footer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 10px;
            font-size: 10px;
            color: #666;
        }
        @media print {
            body {
                max-width: none;
                padding: 10px;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="company-info">
            ${companyAddress}<br>
            ${companyPhone}<br>
            ${companyEmail}
        </div>
        <div class="receipt-title">RECEIPT</div>
    </div>

    <div class="order-info">
        <div class="info-row">
            <span>Order #:</span>
            <span>${sale.orderNumber || 'N/A'}</span>
        </div>
        <div class="info-row">
            <span>Date:</span>
            <span>${this.formatDate(sale.orderDate)}</span>
        </div>
        <div class="info-row">
            <span>Customer:</span>
            <span>${sale.customerName || 'Walk-in'}</span>
        </div>
        <div class="info-row">
            <span>Type:</span>
            <span>${sale.orderTypeConfig?.label || 'N/A'}</span>
        </div>
        <div class="info-row">
            <span>Branch:</span>
            <span>${sale.branchName || 'N/A'}</span>
        </div>
    </div>

    <div class="items-section">
        <div class="items-header">
            <div style="display: flex; justify-content: space-between;">
                <span>Item</span>
                <span>Qty x Price</span>
            </div>
        </div>
        ${items.map((item, index) => {
          // Handle different field structures for item data
          const itemName = item.name || 
                          item.productName || 
                          item.product_name || 
                          item.product?.name || 
                          item.productDetails?.name ||
                          item.itemName || 
                          item.title || 
                          item.description || 
                          `Item #${index + 1}`;
          
          const itemQty = item.quantity || item.qty || 1;
          const itemPrice = item.price || 
                           item.unitPrice || 
                           item.product?.outletPrice || 
                           item.productDetails?.outletPrice || 
                           0;
          
          return `
            <div class="item-row">
                <span class="item-name">${itemName}</span>
                <span class="item-qty-price">${itemQty} x ${this.formatCurrency(itemPrice)}</span>
            </div>
          `;
        }).join('')}
    </div>

    <div class="totals-section">
        <div class="total-row">
            <span>Subtotal:</span>
            <span>${this.formatCurrency(subtotal)}</span>
        </div>
        ${discount > 0 ? `
        <div class="total-row">
            <span>Discount:</span>
            <span>-${this.formatCurrency(discount)}</span>
        </div>
        ` : ''}
        ${tax > 0 ? `
        <div class="total-row">
            <span>Tax/Service:</span>
            <span>${this.formatCurrency(tax)}</span>
        </div>
        ` : ''}
        <div class="total-row final">
            <span>TOTAL:</span>
            <span>${this.formatCurrency(total)}</span>
        </div>
    </div>

    <div class="payment-info">
        <div class="info-row">
            <span>Payment Method:</span>
            <span>${sale.paymentMethodConfig?.label || 'Cash'}</span>
        </div>
        <div class="info-row">
            <span>Status:</span>
            <span>${sale.paymentStatusConfig?.label || 'Paid'}</span>
        </div>
    </div>

    <div class="footer">
        Thank you for your business!<br>
        Generated: ${new Date().toLocaleString()}
    </div>
</body>
</html>
    `.trim()
  }

  /**
   * Format date for display
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date
   */
  static formatDate(date) {
    if (!date) return 'N/A'
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return dateObj.toLocaleString()
    } catch {
      return 'Invalid Date'
    }
  }

  /**
   * Format currency value
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency
   */
  static formatCurrency(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) return 'Rs.0.00'
    return `Rs.${amount.toFixed(2)}`
  }

  /**
   * Print receipt using hidden iframe (same tab)
   * @param {Object} sale - Sale data
   * @param {Object} options - Options for receipt generation
   */
  static printReceipt(sale, options = {}) {
    const htmlContent = this.generateReceiptHTML(sale, options)
    
    // Create hidden iframe for printing
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = 'none'
    
    document.body.appendChild(iframe)
    
    // Write content to iframe
    const iframeDoc = iframe.contentWindow.document
    iframeDoc.open()
    iframeDoc.write(htmlContent)
    iframeDoc.close()
    
    // Trigger print dialog
    setTimeout(() => {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
      
      // Clean up after print dialog closes
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)
    }, 250)
  }

  /**
   * Download receipt as HTML file
   * @param {Object} sale - Sale data
   * @param {string} filename - Optional filename
   * @param {Object} options - Options for receipt generation
   */
  static downloadHTML(sale, filename = null, options = {}) {
    const htmlContent = this.generateReceiptHTML(sale, options)
    const blob = new Blob([htmlContent], { type: 'text/html' })
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `receipt_${sale.orderNumber || Date.now()}.html`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}